import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { TextField } from "@/components/TextField";
import { SubmitButton } from "@/components/SubmitButton";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { resetPassword } from "@/lib/brokerAuth";
import { ApiError } from "@/lib/api";

// Step 2 of the forgotten-password flow: confirm the emailed code + a new
// password. Email is carried over from the forgot step (editable, since a broker
// may deep-link straight here). The neutral "we sent a code" banner only shows
// when arriving from the forgot step (state.sent) — it never asserts the account
// exists. Success routes back to /login with a notice to sign in fresh.
type ResetState = { email?: string; sent?: boolean } | null;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = location.state as ResetState;

  const [email, setEmail] = useState(incoming?.email ?? "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setError(null);
    setPending(true);
    try {
      await resetPassword({ email, code, newPassword });
      navigate("/login", {
        replace: true,
        state: { notice: "Password reset. Sign in with your new password." },
      });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title="Enter your reset code"
      description="Check your email for a verification code, then choose a new password."
      footer={
        <Link to="/login" className="text-brk-accent hover:underline">
          Back to sign in
        </Link>
      }
    >
      {incoming?.sent && (
        <p role="status" className="text-center text-sm text-brk-dim">
          If an account exists for that email, we’ve sent a reset code.
        </p>
      )}
      <form className="flex flex-col gap-4" onSubmit={onSubmit}>
        <TextField
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <TextField
          label="Verification code"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          required
        />
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
        />
        <PasswordRequirements value={newPassword} />
        <TextField
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
        />
        {error && (
          <p role="alert" className="text-sm text-brk-danger">
            {error}
          </p>
        )}
        <SubmitButton pending={pending} pendingLabel="Resetting…">
          Reset password
        </SubmitButton>
      </form>
    </AuthLayout>
  );
}
